<template>
  <div class="container pull-up">
    <div class="row">
      <div class="col-md-12">
        <div class="card m-b-30">
          <div class="card-header">
            <h5 class="m-b-0">
              <i class="mdi mdi-checkbox-intermediate"></i> {{ $t('pos.button.all') }}
            </h5>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="w-full border-collapse">
                <thead class="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Store</th>
                    <!-- <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th> -->
                    <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked On</th>
                    <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr
                    v-for="pos in poses"
                    :key="pos.id"
                    @click="openDetailPanel(pos)"
                    class="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                  >
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm font-medium text-gray-900">{{ pos.name }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm text-gray-900">{{ pos.store?.name || 'N/A' }}</div>
                      <div class="text-xs text-gray-500">{{ pos.store?.code || '' }}</div>
                    </td>
                    <!-- <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm text-gray-900 font-mono">{{ pos.code }}</div>
                    </td> -->
                    <td class="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        :class="pos.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                        class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                      >
                        {{ pos.active ? $t('label.active') : $t('label.inactive') }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm text-gray-900">{{ pos.device_name || 'N/A' }}</div>
                      <div v-if="pos.device_id" class="text-xs text-gray-500 font-mono truncate max-w-xs">{{ pos.device_id }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span
                        :class="{
                          'bg-blue-100 text-blue-800': pos.platform === 'ios',
                          'bg-green-100 text-green-800': pos.platform === 'android',
                          'bg-gray-100 text-gray-800': !pos.platform || (pos.platform !== 'ios' && pos.platform !== 'android')
                        }"
                        class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full capitalize"
                      >
                        {{ pos.platform || 'Unknown' }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm text-gray-900">{{ formatDate(pos.linked_on) }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-center" @click.stop>
                      <el-dropdown
                        v-can.event="'pos.update'"
                        @command="handleAction"
                        trigger="click"
                        split-button
                        type="primary"
                        size="small"
                      >
                      <i class="el-icon-edit"></i> {{ $t('button.edit') }}
                      <el-dropdown-menu slot="dropdown">
                          <el-dropdown-item
                            v-can.disable="'pos.change-status'"
                            :command="{ action: 'changeStatus', pos: pos }"
                          >
                          <i class="mdi mdi-archive"></i> {{ $t('pos.button.change_status') }}
                        </el-dropdown-item>
                          <el-dropdown-item
                            v-can.disable="'pos.delete'"
                            :command="{ action: 'delete', pos: pos }"
                            class="text-danger"
                          >
                          <i class="mdi mdi-delete"></i> {{ $t('button.delete') }}
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="poses.length === 0 && !loading" class="text-center py-12">
                <p class="text-gray-500 text-sm">No POS systems found</p>
              </div>
            </div>
            <!-- Pagination -->
            <div class="text-center pt-4 mt-4 border-t border-gray-200" v-if="total > 0 || poses.length > 0">
              <Pagination
                :total="total"
                :current-page="query.page"
                :page-size="+query.per_page"
                :page-sizes="[10, 20, 30, 40, 50, 100]"
                layout="prev, pager, next, sizes"
                background
                :hide-on-single-page="false"
                @current-change="handlePageChange"
                @size-change="handlePageSizeChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Dialog -->
    <edit-pos
      :display="displayEditDialog"
      @close="closeEditDialog"
      @updated="posUpdate"
      @created="newPOS"
      :pos="pos"
    ></edit-pos>

    <!-- Detail Panel Slide-out -->
    <div
      v-if="showDetailPanel"
      class="fixed inset-0 z-50 overflow-hidden"
      @click.self="closeDetailPanel"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
        @click="closeDetailPanel"
      ></div>

      <!-- Slide-out Panel (from left) -->
      <div
        class="absolute left-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out overflow-y-auto"
        :class="showDetailPanel ? 'translate-x-0' : '-translate-x-full'"
      >
        <!-- Header -->
        <div class="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 shadow-md z-10">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-xl font-semibold">{{ selectedPos?.name || 'POS Details' }}</h2>
              <p class="text-blue-100 text-sm mt-1">Complete Information</p>
            </div>
            <button
              @click="closeDetailPanel"
              class="text-white hover:text-gray-200 transition-colors p-2 rounded-full hover:bg-blue-800"
              aria-label="Close panel"
            >
              <i class="mdi mdi-close text-2xl"></i>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="p-6" v-if="selectedPos">
          <!-- Basic Information -->
          <div class="mb-8">
            <h3 class="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
              Basic Information
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Name</label>
                <p class="mt-1 text-sm font-semibold text-gray-900">{{ selectedPos.name }}</p>
              </div>
              <!-- <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Code</label>
                <p class="mt-1 text-sm font-mono text-gray-900">{{ selectedPos.code }}</p>
              </div> -->
              <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</label>
                <p class="mt-1">
                  <span
                    :class="selectedPos.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                    class="px-3 py-1 inline-flex text-sm font-semibold rounded-full"
                  >
                    {{ selectedPos.active ? $t('label.active') : $t('label.inactive') }}
                  </span>
                </p>
              </div>
              <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">POS ID</label>
                <p class="mt-1 text-sm text-gray-900">{{ selectedPos.id }}</p>
              </div>
            </div>
          </div>

          <!-- Store Information -->
          <div class="mb-8" v-if="selectedPos.store">
            <h3 class="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
              Store Information
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Store Name</label>
                <p class="mt-1 text-sm font-semibold text-gray-900">{{ selectedPos.store.name }}</p>
              </div>
              <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Store Code</label>
                <p class="mt-1 text-sm font-mono text-gray-900">{{ selectedPos.store.code }}</p>
              </div>
              <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Store ID</label>
                <p class="mt-1 text-sm text-gray-900">{{ selectedPos.store.id }}</p>
              </div>
            </div>
          </div>

          <!-- Device Information -->
          <div class="mb-8" v-if="selectedPos.device_id || selectedPos.device_name">
            <h3 class="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
              Device Information
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Device Name</label>
                <p class="mt-1 text-sm font-semibold text-gray-900">{{ selectedPos.device_name || 'N/A' }}</p>
              </div>
              <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Platform</label>
                <p class="mt-1">
                  <span
                    :class="{
                      'bg-blue-100 text-blue-800': selectedPos.platform === 'ios',
                      'bg-green-100 text-green-800': selectedPos.platform === 'android',
                      'bg-gray-100 text-gray-800': !selectedPos.platform || (selectedPos.platform !== 'ios' && selectedPos.platform !== 'android')
                    }"
                    class="px-3 py-1 inline-flex text-sm font-semibold rounded-full capitalize"
                  >
                    {{ selectedPos.platform || 'Unknown' }}
                  </span>
                </p>
              </div>
              <div class="bg-gray-50 rounded-lg p-4 md:col-span-2">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Device ID</label>
                <p class="mt-1 text-sm font-mono text-gray-900 break-all">{{ selectedPos.device_id || 'N/A' }}</p>
              </div>
            </div>
          </div>

          <!-- User Information -->
          <div class="mb-8" v-if="selectedPos.user">
            <h3 class="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
              User Information
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Name</label>
                <p class="mt-1 text-sm font-semibold text-gray-900">{{ selectedPos.user.name || 'N/A' }}</p>
              </div>
              <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Username</label>
                <p class="mt-1 text-sm text-gray-900">{{ selectedPos.user.username || 'N/A' }}</p>
              </div>
              <div class="bg-gray-50 rounded-lg p-4" v-if="selectedPos.user.email">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
                <p class="mt-1 text-sm text-gray-900">{{ selectedPos.user.email }}</p>
              </div>
            </div>
          </div>

          <!-- Timestamps -->
          <div class="mb-8">
            <h3 class="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
              Timestamps
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Created At</label>
                <p class="mt-1 text-sm text-gray-900">{{ formatDateTime(selectedPos.created_at) }}</p>
              </div>
              <div class="bg-gray-50 rounded-lg p-4">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Updated At</label>
                <p class="mt-1 text-sm text-gray-900">{{ formatDateTime(selectedPos.updated_at) }}</p>
              </div>
              <div class="bg-gray-50 rounded-lg p-4" v-if="selectedPos.linked_on">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Linked On</label>
                <p class="mt-1 text-sm text-gray-900">{{ formatDateTime(selectedPos.linked_on) }}</p>
              </div>
              <div class="bg-gray-50 rounded-lg p-4" v-if="selectedPos.linked_by">
                <label class="text-xs font-medium text-gray-500 uppercase tracking-wide">Linked By</label>
                <p class="mt-1 text-sm text-gray-900">User ID: {{ selectedPos.linked_by }}</p>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex gap-3 pt-4 border-t border-gray-200">
            <button
              v-can="'pos.update'"
              @click="editSelectedPos"
              class="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-150 flex items-center justify-center gap-2"
            >
              <i class="mdi mdi-pencil"></i>
              <span>{{ $t('button.edit') }}</span>
            </button>
            <button
              v-can="'pos.change-status'"
              @click="changeStatusSelectedPos"
              class="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-150 flex items-center justify-center gap-2"
            >
              <i class="mdi mdi-archive"></i>
              <span>{{ $t('pos.button.change_status') }}</span>
            </button>
            <button
              v-can="'pos.delete'"
              @click="deleteSelectedPos"
              class="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-150 flex items-center justify-center gap-2"
            >
              <i class="mdi mdi-delete"></i>
              <span>{{ $t('button.delete') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CreateEditPOS from './Modal/CreateEditPOS.vue'
import Pagination from '../_Partials/Pagination.vue'

export default {
  name: 'Index',
  components: {
    'edit-pos': CreateEditPOS,
    Pagination
  },
  data () {
    return {
      loading: false,
      poses: [],
      stores: [],
      pos: {},
      displayEditDialog: false,
      showDetailPanel: false,
      selectedPos: null,
      query: {
        page: 1,
        per_page: 10
      },
      total: 0
    }
  },
  methods: {
    formatDate (dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    formatDateTime (dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    openDetailPanel (pos) {
      this.selectedPos = pos
      this.showDetailPanel = true
      // Prevent body scroll when panel is open
      document.body.style.overflow = 'hidden'
    },
    closeDetailPanel () {
      this.showDetailPanel = false
      this.selectedPos = null
      // Restore body scroll
      document.body.style.overflow = ''
    },
    editSelectedPos () {
      this.closeDetailPanel()
      this.showEditDialog(this.selectedPos.id)
    },
    changeStatusSelectedPos () {
      this.askQuestion(this.$t('pos.confirm.status_change'), async () => {
        try {
          await this.withLoading(async () => {
            const data = await this.$posService.changePOSStatus(this.selectedPos.id)
            this.posUpdate(data)
            this.closeDetailPanel()
            this.loading = false
            this.notify(this.$t('pos.message.status_changed'), this.$t('status.success'))
          }, 'loading')
        } catch (error) {
          console.log('Component caught error in changeStatusSelectedPos:', error)
        }
      })
    },
    deleteSelectedPos () {
      this.askQuestion(this.$t('pos.confirm.delete'), async () => {
        try {
          await this.withLoading(async () => {
            await this.$posService.deletePOS(this.selectedPos.id)
            const index = this.poses.findIndex(p => p.id === this.selectedPos.id)
            if (index > -1) {
              this.poses.splice(index, 1)
              this.total--
            }
            // If current page is empty and not first page, go to previous page
            if (this.poses.length === 0 && this.query.page > 1) {
              this.query.page--
            }
            this.closeDetailPanel()
            // Refresh the list to ensure pagination is correct
            await this.fetchPoses()
            this.loading = false
            this.notify(this.$t('pos.message.deleted'), this.$t('status.success'))
          }, 'loading')
        } catch (error) {
          console.log('Component caught error in deleteSelectedPos:', error)
        }
      })
    },
    handleAction (command) {
      const { action, pos } = command
      if (action === 'changeStatus') {
        this.posChangeStatus(null, pos)
      } else if (action === 'delete') {
        this.posDelete(null, pos.id)
      }
    },
    updateStore (storeId, posId) {
      this.$posService.changeStore(posId, storeId)
        .then(res => {
          this.notify(this.$t('pos.message.store'))
          const pos = this.poses.find(p => p.id === posId)
          if (pos) {
            pos.store_id = storeId
            // Update store object if available
            const store = this.stores.find(s => s.id === storeId)
            if (store) {
              pos.store = store
            }
          }
        })
        .catch(this.globalErrHandle)
    },
    showEditDialog (id) {
      this.pos = this.poses.find(d => d.id === id)
      this.displayEditDialog = true
    },
    closeEditDialog () {
      this.displayEditDialog = false
    },
    newPOS (data) {
      this.poses.splice(0, 0, data)
      this.total++
      // Reset to first page to show the new item
      if (this.query.page !== 1) {
        this.query.page = 1
        this.fetchPoses()
      }
    },
    posUpdate (data) {
      const index = this.poses.findIndex(d => d.id === data.id)
      if (index > -1) {
        this.poses.splice(index, 1, data)
      }
      // Update selected pos if it's the same one
      if (this.selectedPos && this.selectedPos.id === data.id) {
        this.selectedPos = data
      }
    },
    posChangeStatus (index, pos) {
      this.askQuestion(this.$t('pos.confirm.status_change'), async () => {
        try {
          await this.withLoading(async () => {
            const data = await this.$posService.changePOSStatus(pos.id)
            this.posUpdate(data)
            this.loading = false
            this.notify(this.$t('pos.message.status_changed'), this.$t('status.success'))
          }, 'loading')
        } catch (error) {
          console.log('Component caught error in posChangeStatus:', error)
        }
      })
    },
    posDelete (index, id) {
      this.askQuestion(this.$t('pos.confirm.delete'), async () => {
        try {
          await this.withLoading(async () => {
            await this.$posService.deletePOS(id)
            const actualIndex = index !== null ? index : this.poses.findIndex(p => p.id === id)
            if (actualIndex > -1) {
              this.poses.splice(actualIndex, 1)
              this.total--
            }
            // If current page is empty and not first page, go to previous page
            if (this.poses.length === 0 && this.query.page > 1) {
              this.query.page--
              this.fetchPoses()
            }
            this.loading = false
            this.notify(this.$t('pos.message.deleted'), this.$t('status.success'))
          }, 'loading')
        } catch (error) {
          console.log('Component caught error in posDelete:', error)
        }
      })
    },
    handlePageChange (page) {
      this.query.page = page
      this.fetchPoses()
    },
    handlePageSizeChange (size) {
      this.query.per_page = size
      this.query.page = 1
      this.fetchPoses()
    },
    async fetchPoses () {
      try {
        await this.withLoading(async () => {
          const data = await this.$posService.getPOSesPaginated(this.query)
          console.log('POS Pagination Data:', data) // Debug log
          this.poses = data.data || []
          this.total = data.total || 0
          console.log('Total POS count:', this.total) // Debug log
          // Update current page/per_page if backend returns them
          if (data.current_page) this.query.page = data.current_page
          if (data.per_page) this.query.per_page = data.per_page
        }, 'loading')
      } catch (error) {
        console.log('Component caught error in fetchPoses:', error)
      }
    }
  },
  async mounted () {
    this.$emit('parent-title', this.$t('pos.title.pos'))
    try {
      const data = await this.$departmentService.getDepartments()
      this.stores = data
    } catch (error) {
      console.log('Component caught error in getDepartments:', error)
    }
    await this.fetchPoses()
  },
  created () {
    this.$parent.$on('new-pos', this.newPOS)
  },
  beforeDestroy () {
    // Clean up body overflow style
    document.body.style.overflow = ''
  }
}
</script>

<style scoped>
/* Custom scrollbar for detail panel */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
